import React, { useEffect, useMemo, useState } from 'react';
import cx from 'classnames';
import { TableCell, TextField } from '@mui/material';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import { RootState } from 'store/types';

import SortingArrow from './SortingArrow';
import { IColumn } from '../types';
import { TableTypes } from '../constants';

import useStyles from '../styles';
import { setFiltering } from 'store/table';

interface TableHeadCellProps {
  column: IColumn;
}

const TableHeadCell: React.FC<TableHeadCellProps> = ({ column }: TableHeadCellProps) => {
  const [search, setSearch] = useState<string>('');

  const dispatch = useAppDispatch();
  const classes = useStyles();
  const { columnsWithoutWidth } = useAppSelector((state: RootState) => state.table);

  useEffect(() => {
    if (search) {
      dispatch(setFiltering({ field: column.key, value: search }));
    } else {
      dispatch(setFiltering({ field: column.key, value: '' }));
    }
  }, [search]);

  const cellContent = useMemo(() => {
    switch (column.searchable) {
      case true:
        return (
          <TextField
            variant="standard"
            label={column.name}
            value={search}
            onChange={e => {
              setSearch(e.target.value);
            }}
            inputProps={{}}
          />
        );
      case false:
        return <>{column.name}</>;
      default:
        return <>{column.name}</>;
    }
  }, [column.searchable, search]);

  const getJustificationForCell = useMemo(() => {
    if (column.type === TableTypes.bulk) {
      return 'center';
    }
    return column.labelAlignment ? column.labelAlignment : 'space-between';
  }, [column.labelAlignment, column.type]);

  const getAlignmentForCell = useMemo(() => {
    if (column.type === TableTypes.bulk) {
      return 'center';
    }
    return 'flex-end';
  }, [column.type]);

  return (
    <TableCell
      className={cx(classes.tableCell, classes.tableRowAligned)}
      style={{
        maxWidth: !column.width ? `calc(100% / ${columnsWithoutWidth})` : `${column.width}px`,
        justifyContent: getJustificationForCell,
        alignItems: getAlignmentForCell,
        maxHeight: '65px',
        width: '100%',
        position: 'relative',
      }}
    >
      {cellContent}

      <>{column.sortable && <SortingArrow sortable={column.sortable} fieldName={column.key} />}</>
    </TableCell>
  );
};

export default TableHeadCell;
